import { Box, Spacer, Stack, Tag, Text, useClipboard, Wrap, WrapItem } from '@chakra-ui/react'
import {
  faBell,
  faBellSlash,
  faBullhorn,
  faCheck,
  faClock,
  faEllipsis,
  faExternalLink,
  faFlag,
  faLink,
  faPencil,
  faThumbtack,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons'
import { faThumbtack as faThumbtackSolid } from '@fortawesome/pro-solid-svg-icons'
import { DateTime } from 'luxon'
import React, { useCallback, useEffect, useState } from 'react'

import { isNotNull, noOp } from '@/utils/helpers'

import { useUser } from '@/client/context/user'
import { useRelativeTimeShort } from '@/client/hooks'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import * as url from '@/client/services/url'
import hasUserAuthScope from '@/client/services/user/hasUserAuthScope'
import isUserInChannel from '@/client/services/worlds/isUserInChannel'
import type { PostStatus } from '@/client/services/worlds/types'

import { POST_HEADER_MENU_CLASS_NAME } from '@/components/Community/Post/constants'
import type { PostDetails } from '@/components/Community/Post/types'
import ShwLink from '@/components/ShwLink'
import { useToaster } from '@/components/Toast/useToaster'
import Icon from '@/components/UI/Icon/Icon'
import Tippy from '@/components/UI/Tippy'
import Avatar from '@/components/Worlds/UI/Avatar/Avatar'
import AvatarPopover from '@/components/Worlds/UI/Avatar/AvatarPopover'
import AvatarWithIcon from '@/components/Worlds/UI/Avatar/AvatarWithIcon'
import AvatarWithName from '@/components/Worlds/UI/Avatar/AvatarWithName'
import { getAugmentedWorldLevel } from '@/components/Worlds/UI/Level/internal/helpers'
import Menu from '@/components/Worlds/UI/Menu/Menu'
import type { MenuItemType } from '@/components/Worlds/UI/Menu/types'

const AVATAR_SECTION_WIDTH_PX = '48px'

type PostHeaderProps = {
  post: PostDetails
  onEdit?: (p: PostDetails) => void
  onDelete?: (p: PostDetails) => void
  onReport: (p: PostDetails) => void
  hideMenu?: boolean
  hideFullViewMenuItem?: boolean
  showMenuOnGroupHover?: boolean
}

function PostHeaderInternal({
  post,
  onEdit = noOp,
  onDelete = noOp,
  onReport,
  hideMenu = false,
  hideFullViewMenuItem = false,
  showMenuOnGroupHover = false,
}: PostHeaderProps): JSX.Element {
  const currentUser = useUser()
  const worldDetails = post.liveHelpInfo?.worldDetails ?? null

  const toast = useToaster()

  const { user, createdAt, lastEditedAt, isPinned, channel, followedAt } = post

  const [optimisticFollowingPost, setOptimisticFollowingPost] = useState(followedAt != null)

  const isAnnouncement = post.type === 'announcement' && post.channel.type !== 'announcement'

  useEffect(() => {
    setOptimisticFollowingPost(followedAt != null)
  }, [followedAt])

  const postUrl = url.getPostUrlFromContext({
    postId: post.id,
    channelId: channel.id,
    hasWorld: worldDetails != null,
  })

  const { onCopy, hasCopied } = useClipboard(postUrl)

  const createdAtRelative = useRelativeTimeShort({ time: createdAt })

  const [handlePinPost] = useAPIWrapper(
    async () => {
      await RPC.admin_pinPost({
        postId: post.id,
      })
      toast({
        title: 'Pinned post!',
        status: 'success',
      })
    },
    [post.id, toast],
    'Pin Post'
  )

  const [handleUnpinPost] = useAPIWrapper(
    async () => {
      await RPC.admin_unpinPost({
        postId: post.id,
      })
      toast({
        title: 'Unpinned post.',
        status: 'success',
      })
    },
    [post.id, toast],
    'Unpin Post'
  )

  const [handleUpdateStatus] = useAPIWrapper(
    async (status: PostStatus) => {
      await RPC.updatePostStatus({
        postId: post.id,
        status,
      })
      toast({
        title: 'Changed post status!',
        status: 'success',
      })
    },
    [post.id, toast],
    'Unpin Post'
  )

  const [handleFollowPost] = useAPIWrapper(
    async () => {
      setOptimisticFollowingPost(true)
      await RPC.followPost({
        postId: post.id,
      })
      toast({
        title: 'You are now receiving notifications for this post!',
        status: 'success',
      })
    },
    [post.id, toast],
    'Follow Post'
  )

  const [handleUnfollowPost] = useAPIWrapper(
    async () => {
      setOptimisticFollowingPost(false)
      await RPC.unfollowPost({
        postId: post.id,
      })
      toast({
        title: 'You are no longer receiving notifications for this post.',
        status: 'success',
      })
    },
    [post.id, toast],
    'Unfollow Post'
  )

  const handleCopy = useCallback(() => {
    onCopy()
    toast({
      title: 'Copied link!',
      status: 'success',
    })
  }, [onCopy, toast])

  const canEditPost =
    currentUser.id === user.id || hasUserAuthScope(currentUser, 'admin:posts:edit')

  const canDeletePost =
    currentUser.id === user.id || hasUserAuthScope(currentUser, 'admin:posts:delete')

  const canEditPostStatus = hasUserAuthScope(currentUser, 'admin:posts:edit')

  const canPinPost = hasUserAuthScope(currentUser, 'admin:posts:pin')

  const canAccessAdminPage = hasUserAuthScope(currentUser, 'admin:posts:read')

  const userWorldLevel = getAugmentedWorldLevel({
    user: post.user,
    level: post?.liveHelpInfo?.userWorldLevel ?? null,
  })

  const menuItems: MenuItemType[] = [
    optimisticFollowingPost
      ? { icon: faBellSlash, label: 'Disable Notifications', onClick: handleUnfollowPost }
      : { icon: faBell, label: 'Enable Notifications', onClick: handleFollowPost },
    { icon: faLink, label: hasCopied ? 'Copied!' : 'Copy Link', onClick: handleCopy },
    hideFullViewMenuItem
      ? null
      : {
          icon: faExternalLink,
          label: 'Open Full View',
          href: postUrl,
        },
    canAccessAdminPage
      ? {
          icon: faExternalLink,
          label: 'Open Admin Page',
          href: url.getAdminUrl(`post/${post.id}`),
        }
      : null,
    { icon: faFlag, label: 'Report Post', onClick: () => onReport(post) },
    canPinPost && !isPinned
      ? { icon: faThumbtack, label: 'Pin To Channel', onClick: () => handlePinPost() }
      : null,
    canPinPost && isPinned
      ? { icon: faThumbtack, label: 'Unpin Post', onClick: handleUnpinPost }
      : null,
    canEditPost ? { icon: faPencil, label: 'Edit Post', onClick: () => onEdit(post) } : null,
    canEditPostStatus && post.channel.type === 'question'
      ? { icon: faCheck, label: 'Mark as Solved', onClick: () => handleUpdateStatus('solved') }
      : null,
    canEditPostStatus && post.channel.type === 'question'
      ? { icon: faClock, label: 'Mark as Closed', onClick: () => handleUpdateStatus('closed') }
      : null,
    canDeletePost
      ? { icon: faTrash, color: 'error.fg', label: 'Delete Post', onClick: () => onDelete(post) }
      : null,
  ].filter(isNotNull)

  const canUserAccessMenu = isUserInChannel({ channelId: post.channel.id, user: currentUser })

  return (
    <Stack direction="row" spacing="12px" justify="space-between">
      <Box width={AVATAR_SECTION_WIDTH_PX}>
        <AvatarPopover user={user} worldSlug={worldDetails?.slug} worldLevel={userWorldLevel}>
          <Box>
            {worldDetails != null ? (
              <AvatarWithIcon iconEntity={worldDetails} user={user} size="md" />
            ) : (
              <Avatar user={user} size="md" />
            )}
          </Box>
        </AvatarPopover>
      </Box>
      <Stack direction="column" spacing="0px">
        <AvatarPopover user={user} worldLevel={userWorldLevel}>
          <Stack direction="row">
            <Box cursor="default">
              <AvatarWithName
                omitAvatar
                user={user}
                variant="icon"
                worldLevel={userWorldLevel}
                world={worldDetails}
              />
            </Box>
            {isAnnouncement ? (
              <Tag backgroundColor="warning.subtle">
                <Stack direction="row" spacing="6px" alignItems="center">
                  <Icon size="sm" faIcon={faBullhorn} omitContainer color="orange.fg" />
                  <Text fontSize="14px" lineHeight="20px" color="orange.fg">
                    Announcement
                  </Text>
                </Stack>
              </Tag>
            ) : null}
          </Stack>
        </AvatarPopover>
        <Wrap spacing="0px">
          <WrapItem my={0}>
            <Stack direction="row" align="center" spacing="4px" mr="4px">
              <Tippy content={createdAt.toLocaleString(DateTime.DATETIME_FULL)}>
                <ShwLink href={postUrl} variant="subtle-gray">
                  {lastEditedAt != null ? `${createdAtRelative} (edited)` : createdAtRelative}
                </ShwLink>
              </Tippy>
              {worldDetails != null ? (
                <Text variant="supporting" color="fg.muted">
                  · {worldDetails.title}
                </Text>
              ) : null}
            </Stack>
          </WrapItem>
        </Wrap>
      </Stack>
      <Spacer />
      {hideMenu || !canUserAccessMenu ? null : (
        <Stack direction="row" spacing="4px" align="center">
          {isPinned ? <Icon faIcon={faThumbtackSolid} size="md" color="orange.focusRing" /> : null}
          <Box display={showMenuOnGroupHover ? 'none' : 'flex'} _groupHover={{ display: 'flex' }}>
            <Menu
              buttonProps={{
                'aria-label': 'Post Actions',
                icon: <Icon faIcon={faEllipsis} size="md" color="fg.muted" />,
              }}
              itemGroups={[menuItems]}
              className={POST_HEADER_MENU_CLASS_NAME}
              strategy="fixed"
            />
          </Box>
        </Stack>
      )}
    </Stack>
  )
}

const PostHeaderMemoized = React.memo(
  PostHeaderInternal,
  (prev, next) =>
    prev.post.id === next.post.id &&
    prev.post.lastEditedAt?.toISO() === next.post.lastEditedAt?.toISO()
)

export default function PostHeader(props: PostHeaderProps): JSX.Element {
  return <PostHeaderMemoized {...props} />
}
